import { CONSTANTS } from '../constants';
import type { IPlayerLogicState } from './player-state';

/**
 * Pure game logic for Player physics and combat.
 * No Phaser dependencies — used by tests and rendering layer.
 */
export class PlayerLogic {
  private state: IPlayerLogicState;
  private isJumpHeld: boolean = false;
  private jumpBufferTimer: number = 0;
  private coyoteTimer: number = 0;
  private gravity: number;
  private acceleration: number;
  private runAcceleration: number;
  private friction: number;
  private airControl: number;

  constructor(
    initialState: IPlayerLogicState,
    gravity: number = CONSTANTS.GRAVITY,
    acceleration: number = CONSTANTS.ACCELERATION,
    runAcceleration: number = CONSTANTS.RUN_ACCELERATION,
    friction: number = CONSTANTS.FRICTION,
    airControl: number = CONSTANTS.AIR_CONTROL
  ) {
    this.state = initialState;
    this.gravity = gravity;
    this.acceleration = acceleration;
    this.runAcceleration = runAcceleration;
    this.friction = friction;
    this.airControl = airControl;
  }

  getState(): IPlayerLogicState {
    return { ...this.state, coins: { ...this.state.coins } };
  }

  setState(patch: Partial<IPlayerLogicState>): void {
    this.state = { ...this.state, ...patch };
  }

  // --- Movement ---
  move(direction: number, isSprinting: boolean = false): void {
    const baseMaxSpeed = isSprinting ? CONSTANTS.SPRINT_SPEED : CONSTANTS.BASE_SPEED;
    const speedBoostMultiplier = this.state.speedBoostDuration > 0 ? 1.5 : 1.0;
    const maxSpeed = baseMaxSpeed * speedBoostMultiplier;
    const accel = isSprinting ? this.runAcceleration : this.acceleration;

    if (this.state.onGround) {
      // Ground movement with acceleration/friction
      if (direction !== 0) {
        if (this.state.vx * direction < maxSpeed) {
          this.state.vx += direction * accel;
        }
        if (Math.abs(this.state.vx) > maxSpeed) {
          this.state.vx = maxSpeed * Math.sign(this.state.vx);
        }
      } else {
        // Apply friction on ground
        if (Math.abs(this.state.vx) < this.friction) {
          this.state.vx = 0;
        } else {
          this.state.vx -= Math.sign(this.state.vx) * this.friction;
        }
      }
    } else {
      // Air movement with airControl (same maxSpeed rules as ground)
      if (direction !== 0) {
        this.state.vx += direction * this.airControl;
        if (Math.abs(this.state.vx) > maxSpeed) {
          this.state.vx = maxSpeed * Math.sign(this.state.vx);
        }
      } else {
        // Less friction in air
        if (Math.abs(this.state.vx) < this.friction * 0.5) {
          this.state.vx = 0;
        } else {
          this.state.vx -= Math.sign(this.state.vx) * this.friction * 0.5;
        }
      }
    }

    // Update facing
    if (direction !== 0) {
      this.state.facing = direction > 0 ? 'right' : 'left';
    }
  }

  // --- Jumping ---
  jump(_pressedDuration: number = 200): void {
    if (!this.state.onGround) return;

    const jumpVelocity = CONSTANTS.JUMP_VELOCITY;
    this.state.vy = jumpVelocity;
    this.state.onGround = false;
    this.state.hasDoubleJumped = false;
    this.state.canDoubleJump = true;
    this.isJumpHeld = true;
    this.coyoteTimer = CONSTANTS.COYOTE_TIME;
    this.jumpBufferTimer = 0;
  }

  releaseJump(): void {
    this.isJumpHeld = false;
    // Variable jump: cutting vertical velocity on release limits rise height.
    // Only meaningful during ascent; called once per key release (not every frame).
    if (this.state.vy < 0) {
      this.state.vy = CONSTANTS.RELEASED_JUMP_VELOCITY;
    }
  }

  doubleJump(): boolean {
    if (this.state.hasDoubleJumped || this.state.onGround) return false;

    const doubleJumpVelocity = CONSTANTS.JUMP_VELOCITY * CONSTANTS.DOUBLE_JUMP_RATIO;
    this.state.vy = doubleJumpVelocity;
    this.state.hasDoubleJumped = true;
    this.state.canDoubleJump = false;
    return true;
  }

  updateJumpBuffer(jumpPressed: boolean, deltaTime: number): void {
    if (jumpPressed && !this.isJumpHeld) {
      this.jumpBufferTimer = CONSTANTS.JUMP_BUFFER_TIME;
    }
    if (this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= deltaTime;
      if (this.jumpBufferTimer <= 0 && !this.state.onGround) {
        // Try to double jump if buffer expires in air
        this.doubleJump();
      }
    }
  }

  // --- Physics update ---
  update(deltaTime: number): void {
    const dt = deltaTime / 16.67; // normalize to ~60fps

    // Apply gravity (only when airborne)
    // GDD 4.1: gravity 0.55 is per-frame at 60fps (not 0.55 * 60)
    if (!this.state.onGround) {
      this.state.vy += this.gravity * dt;

      // Terminal velocity
      if (this.state.vy > CONSTANTS.TERMINAL_VELOCITY) {
        this.state.vy = CONSTANTS.TERMINAL_VELOCITY;
      }
    }

    // Update position
    this.state.x += this.state.vx * dt;
    this.state.y += this.state.vy * dt;

    // Coyote time
    if (this.state.onGround) {
      this.coyoteTimer = CONSTANTS.COYOTE_TIME;
    } else if (this.coyoteTimer > 0) {
      this.coyoteTimer -= deltaTime;
    }

    // Jump buffer
    if (this.jumpBufferTimer > 0) {
      this.jumpBufferTimer -= deltaTime;
    }

    // Update invulnerability timer
    if (this.state.isInvulnerable) {
      this.state.invulnerabilityTimer -= deltaTime;
      if (this.state.invulnerabilityTimer <= 0) {
        this.state.isInvulnerable = false;
      }
    }

    // Update speed boost timer
    if (this.state.speedBoostDuration > 0) {
      this.state.speedBoostDuration -= deltaTime;
      if (this.state.speedBoostDuration < 0) {
        this.state.speedBoostDuration = 0;
      }
    }

    // Update hurt timer
    if (this.state.isHurt) {
      this.state.hurtTimer -= deltaTime;
      if (this.state.hurtTimer <= 0) {
        this.state.isHurt = false;
      }
    }

    // Update stone cooldown
    if (this.state.stoneCooldown > 0) {
      this.state.stoneCooldown -= dt;
      if (this.state.stoneCooldown < 0) {
        this.state.stoneCooldown = 0;
      }
    }

    // Reset double jump on ground
    if (this.state.onGround) {
      this.state.canDoubleJump = true;
      this.state.hasDoubleJumped = false;
    }
  }

  setOnGround(onGround: boolean): void {
    this.state.onGround = onGround;
    if (onGround) {
      this.state.canDoubleJump = true;
      this.state.hasDoubleJumped = false;
    }
  }

  // --- Stone throwing ---
  throwStone(): boolean {
    if (this.state.stoneCooldown > 0) return false;
    if (this.state.activeStones >= CONSTANTS.STONE_MAX_ACTIVE) return false;
    if (this.state.stones <= 0) return false; // out of ammo

    this.state.stoneCooldown = CONSTANTS.STONE_COOLDOWN;
    this.state.activeStones += 1;
    this.state.stones -= 1;
    return true;
  }

  /** Grant stone ammo (powerups, level start). */
  addStones(amount: number): void {
    this.state.stones = Math.max(0, this.state.stones + amount);
  }

  removeStone(): void {
    if (this.state.activeStones > 0) {
      this.state.activeStones -= 1;
    }
  }

  // --- Damage ---
  takeDamage(amount: number): boolean {
    if (this.state.isInvulnerable) return false;

    this.state.hp -= amount;
    this.state.isHurt = true;
    this.state.hurtTimer = 200;

    // Invulnerability
    this.state.isInvulnerable = true;
    this.state.invulnerabilityTimer = CONSTANTS.DAMAGE_INVULNERABILITY;

    return this.state.hp <= 0;
  }

  // --- Pit respawn ---
  checkPit(pitThreshold: number = CONSTANTS.PIT_THRESHOLD): boolean {
    if (this.state.y > pitThreshold) {
      // Pit damage without triggering full invulnerability block
      this.state.hp -= CONSTANTS.PIT_DAMAGE;
      if (this.state.hp < 0) this.state.hp = 0;
      this.respawnAtCheckpoint();
      return true;
    }
    return false;
  }

  respawnAtCheckpoint(): void {
    this.state.x = this.state.checkpointX;
    this.state.y = this.state.checkpointY;
    this.state.vx = 0;
    this.state.vy = 0;
    this.state.isInvulnerable = true;
    this.state.invulnerabilityTimer = CONSTANTS.DAMAGE_INVULNERABILITY;
  }

  updateCheckpoint(x: number, y: number): void {
    this.state.checkpointX = x;
    this.state.checkpointY = y;
    this.state.lastSafeX = x;
    this.state.lastSafeY = y;
  }

  // --- Currency ---
  addCurrency(type: 'CUP' | 'USD' | 'EUR', amount: number): void {
    switch (type) {
      case 'CUP':
        this.state.coins.cup += amount;
        break;
      case 'USD':
        this.state.coins.usd += amount;
        break;
      case 'EUR':
        this.state.coins.eur += amount;
        break;
    }
  }

  applySpeedBoost(duration: number): void {
    this.state.speedBoostDuration = Math.max(this.state.speedBoostDuration, duration);
  }

  valueInCup(): number {
    return this.state.coins.cup +
      this.state.coins.usd * CONSTANTS.USD_TO_CUP +
      this.state.coins.eur * CONSTANTS.EUR_TO_CUP;
  }

  // --- Helpers ---
  getHitbox(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.state.x - CONSTANTS.PLAYER_HITBOX_WIDTH / 2,
      y: this.state.y - CONSTANTS.PLAYER_HITBOX_HEIGHT,
      width: CONSTANTS.PLAYER_HITBOX_WIDTH,
      height: CONSTANTS.PLAYER_HITBOX_HEIGHT,
    };
  }

  checkPlatforms(platforms: Array<{ x: number; y: number; width: number; height: number }>): void {
    const playerHitbox = this.getHitbox();
    const playerBottom = playerHitbox.y + playerHitbox.height;
    const playerLeft = playerHitbox.x;
    const playerRight = playerHitbox.x + playerHitbox.width;
    for (const plat of platforms) {
      const platTop = plat.y;
      const platLeft = plat.x;
      const platRight = plat.x + plat.width;
      const platBottom = plat.y + plat.height;
      // Tolerancia horizontal 8px para no caer al mínimo desliz
      const horizontallyOver = playerRight > platLeft - 8 && playerLeft < platRight + 8;
      if (!horizontallyOver) continue;
      if (this.state.onGround && Math.abs(playerBottom - platTop) <= 3) {
        this.state.y = platTop;
        this.state.vy = 0;
        this.state.onGround = true;
        return;
      }
      if (this.state.vy > 0 && playerBottom >= platTop && playerBottom <= platTop + this.state.vy + 4 && playerBottom <= platBottom + 4) {
        this.state.y = platTop;
        this.state.vy = 0;
        this.state.onGround = true;
        return;
      }
    }
  }

  getVelocity(): { vx: number; vy: number } {
    return { vx: this.state.vx, vy: this.state.vy };
  }

  isDead(): boolean {
    return this.state.hp <= 0;
  }
}
